import { BRUNI, NIGHTMARES, STRUCTURES } from '@somnhold/content';
import { iatan2, idiv, imax, mulBp, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, Structure, UnsleepingState } from '../../types.ts';
import { damageHero, damageStructure, ev } from '../combat.ts';
import { knockBack } from '../enemies.ts';
import { blowOf } from '../kits.ts';
import { towerRange } from '../vigil.ts';
import { at, by, heroesNear, held, holdable, hurt, isTower, markBlast, said, standing, type Kit } from './kit.ts';

/*
 * Bruni, the Hollow Forger (artificer): a dwarf of Hilde's clan who died at the forge. He shoots from afar with a
 * forge-cannon (the towers in his reach when no hero is), every structure near him turns brittle and takes his
 * blows triple; a riveted bolt throws a hero back and stuns it; his black mortar marks a blast under each hero; and
 * the forge turned (his ultimate) takes the towers by the heroes and shoots them with the bastion's own bolts —
 * from his last phase, released, they burst.
 */

// cds: riveted bolt, black mortar. k: the last tick a tower he took is let go (0: none held).
const RIVET = 0;
const MORTAR = 1;
const RELEASE = 0;

/** A hero this far from a tower is minding it (he shoots the towers left alone first). */
const MINDED = 1500;

/** The hero he shoots at: the nearest standing in his reach. */
const inReach = (s: BattleState, e: Enemy): Hero | null => {
  const r = NIGHTMARES[e.kind].reach;
  let best: Hero | null = null;
  let bd = 0;
  for (const h of s.heroes) {
    if (!standing(h) || !withinRange(e.x, e.y, h.x, h.y, r)) continue;
    const d = (h.x - e.x) * (h.x - e.x) + (h.y - e.y) * (h.y - e.y);
    if (!best || d < bd) {
      best = h;
      bd = d;
    }
  }
  return best;
};

/** The tower he shoots at with no hero in reach: the nearest left alone, else the nearest. */
const towerInReach = (s: BattleState, e: Enemy): Structure | null => {
  const r = NIGHTMARES[e.kind].reach;
  let best: Structure | null = null;
  let bk = 0;
  for (const st of s.structures) {
    if (!isTower(st)) continue;
    const c = at(st);
    if (!withinRange(e.x, e.y, c.x, c.y, r)) continue;
    const minded = heroesNear(s, c.x, c.y, MINDED).length > 0;
    const d = (c.x - e.x) * (c.x - e.x) + (c.y - e.y) * (c.y - e.y);
    // Left alone first (the whole reach squared is less than this).
    const k = (minded ? 0x40000000 : 0) + idiv(d, 1024);
    if (!best || k < bk) {
      best = st;
      bk = k;
    }
  }
  return best;
};

/** His forge-cannon: at a hero in reach, else a tower; he stands to shoot. True while something holds him there. */
const shoot = (s: BattleState, e: Enemy): boolean => {
  const h = inReach(s, e);
  if (h) {
    e.facing = iatan2(h.y - e.y, h.x - e.x);
    if (e.cd > 0) return true;
    e.cd = e.reload;
    e.anim = 'attack';
    e.animT = 0;
    bruni.strike(s, e, h, blowOf(s, e, e.damage));
    return true;
  }
  const st = towerInReach(s, e);
  if (!st) return false;
  const c = at(st);
  e.facing = iatan2(c.y - e.y, c.x - e.x);
  if (e.cd > 0) return true;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  damageStructure(s, st, mulBp(blowOf(s, e, e.damage), BRUNI.structureBp), e);
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: c.x, ty: c.y, kind: 'bruniShot' });
  return true;
};

/** The riveted bolt: at the nearest hero his control may hold (the one he hunts first), thrown back and stunned. */
const rivet = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const r = BRUNI.rivet;
  let tgt: Hero | null = null;
  let best = 0;
  for (const h of s.heroes) {
    if (!holdable(s, h) || !withinRange(e.x, e.y, h.x, h.y, r.range)) continue;
    const d = (h.pid === u.hunt ? 0 : 0x40000000) + idiv((h.x - e.x) * (h.x - e.x) + (h.y - e.y) * (h.y - e.y), 1024);
    if (!tgt || d < best) {
      tgt = h;
      best = d;
    }
  }
  if (!tgt) {
    u.cds[RIVET] = 6;
    return false;
  }
  u.cds[RIVET] = r.every;
  e.facing = iatan2(tgt.y - e.y, tgt.x - e.x);
  e.anim = 'attack';
  e.animT = 0;
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: tgt.x, ty: tgt.y, kind: 'bruniRivet' });
  said(s, e, u, 'Rivet', tgt.x, tgt.y);
  hurt(s, e, u, tgt, r.damage, r.maxHpBp);
  if (!standing(tgt)) return true;
  knockBack(s, e, tgt, { far: r.knock, ticks: 6, immune: 0 });
  tgt.stun = imax(tgt.stun, r.stun);
  held(s, tgt);
  return true;
};

/** The black mortar: a marked blast under each hero in range (the one he hunts first, then the nearest). */
const mortar = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const m = BRUNI.mortar;
  const near = s.heroes.filter((h) => standing(h) && withinRange(e.x, e.y, h.x, h.y, m.range));
  if (!near.length) {
    u.cds[MORTAR] = 6;
    return;
  }
  near.sort((a, b) => {
    const ha = a.pid === u.hunt ? 0 : 1;
    const hb = b.pid === u.hunt ? 0 : 1;
    if (ha !== hb) return ha - hb;
    const da = (a.x - e.x) * (a.x - e.x) + (a.y - e.y) * (a.y - e.y);
    const db = (b.x - e.x) * (b.x - e.x) + (b.y - e.y) * (b.y - e.y);
    return da - db || a.id - b.id;
  });
  for (const h of near.slice(0, m.shells)) markBlast(s, u, h.x, h.y, { warn: m.warn, radius: m.radius, damage: m.damage, hpBp: m.maxHpBp, towerBp: m.towerBp });
  u.cds[MORTAR] = m.every;
  e.anim = 'cast';
  e.animT = 0;
  said(s, e, u, 'Mortar');
};

/** A tower he can take: one that shoots (not a beacon or the belfry), not his already. */
const takeable = (s: BattleState, st: Structure): boolean => isTower(st) && st.type !== 'beacon' && st.type !== 'belfry' && (st.turnedUntil ?? 0) <= s.tick;

/** A tower he took is let go: from his last phase it bursts on the heroes by it. */
const release = (s: BattleState, e: Enemy, u: UnsleepingState, st: Structure): void => {
  const c = at(st);
  if (e.phase < 2 || st.destroyed) {
    ev(s, { t: 'boom', x: c.x, y: c.y, r: 600, kind: 'bruniRelease' });
    return;
  }
  const b = BRUNI.forge.burst;
  for (const h of heroesNear(s, c.x, c.y, b.radius)) hurt(s, e, u, h, b.damage, b.maxHpBp);
  damageStructure(s, st, imax(1, mulBp(st.maxHp, b.towerBp)), e);
  ev(s, { t: 'boom', x: c.x, y: c.y, r: b.radius, kind: 'bruniBurst' });
};

export const bruni: Kit = {
  init: (_s, _e, u) => {
    u.cds = [idiv(BRUNI.rivet.every, 2), idiv(BRUNI.mortar.every, 2)];
    u.k = [0];
  },
  act: (s, e, u) => {
    if (u.cds[RIVET] === 0 && rivet(s, e, u)) return true;
    if (u.cds[MORTAR] === 0) mortar(s, e, u);
    return shoot(s, e);
  },
  // The towers within his range with the most heroes in their reach (none with no hero in any's reach).
  aim: (s, e, u) => {
    const f = BRUNI.forge;
    const n = e.phase >= 1 ? f.towersP1 : f.towers;
    const cands: { st: Structure; k: number; d: number }[] = [];
    for (const st of s.structures) {
      if (!takeable(s, st)) continue;
      const c = at(st);
      if (!withinRange(e.x, e.y, c.x, c.y, f.range)) continue;
      const k = heroesNear(s, c.x, c.y, towerRange(s, st, STRUCTURES[st.type].range)).length;
      if (k > 0) cands.push({ st, k, d: (c.x - e.x) * (c.x - e.x) + (c.y - e.y) * (c.y - e.y) });
    }
    if (!cands.length) return false;
    cands.sort((a, b) => b.k - a.k || a.d - b.d || a.st.id - b.st.id);
    const took = cands.slice(0, n).map((x) => x.st);
    u.tgts = took.map((st) => st.id);
    u.spots = took.map((st) => ({ ...at(st), r: f.mark }));
    const c0 = at(took[0] as Structure);
    u.castA = iatan2(c0.y - e.y, c0.x - e.x);
    e.facing = u.castA;
    said(s, e, u, 'Forge');
    return true;
  },
  land: (s, e, u) => {
    const until = s.tick + BRUNI.forge.ticks;
    for (const st of s.structures) {
      if (!u.tgts.includes(st.id) || st.destroyed) continue;
      st.turnedUntil = until;
      u.k[RELEASE] = until;
      const c = at(st);
      ev(s, { t: 'beam', x: e.x, y: e.y, tx: c.x, ty: c.y, kind: 'bruniSeize' });
    }
    u.tgts = [];
    ev(s, { t: 'boom', x: e.x, y: e.y, r: BRUNI.forge.range, kind: 'bruniForge' });
  },
  // His forge-bolt: by the hero's own health, through armour and shields.
  strike: (s, e, h, dmg) => {
    ev(s, { t: 'beam', x: e.x, y: e.y, tx: h.x, ty: h.y, kind: 'bruniShot' });
    damageHero(s, h, by(h, dmg, BRUNI.blowMaxHpBp), e, undefined, true);
  },
  status: (s, e, u) => {
    // The hollow forge: the structures around him turn brittle.
    const b = BRUNI.brittle;
    if ((s.tick + e.id) % b.every === 0) {
      for (const st of s.structures) {
        if (st.destroyed || st.maxHp <= 0) continue;
        const c = at(st);
        if (withinRange(e.x, e.y, c.x, c.y, b.radius)) st.brittleUntil = imax(st.brittleUntil ?? 0, s.tick + b.ticks);
      }
    }
    // The towers he took, let go as their time runs out.
    const end = u.k[RELEASE] ?? 0;
    if (end <= 0) return;
    for (const st of s.structures) if (st.turnedUntil === s.tick) release(s, e, u, st);
    if (s.tick >= end) u.k[RELEASE] = 0;
  },
};
